import React, { useMemo } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Card, CardContent, Typography } from "@material-ui/core";
import { summariseAccounts } from "../../utils/account-summary";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	card: {
		flex: "1 1 140px",
		maxWidth: 168,
		cursor: "pointer",
		border: "2px solid transparent",
		"&:focus": {
			outline: "none",
			borderColor: theme.palette.brand,
		},
	},
	selected: {
		borderColor: theme.palette.brand,
		backgroundColor: "rgba(214, 221, 55, 0.35)",
	},
	content: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(0.75, 1.25),
		"&:last-child": {
			paddingBottom: theme.spacing(0.75),
		},
	},
	label: {
		color: "#999999",
		fontSize: "0.8125rem",
		lineHeight: 1.2,
	},
	value: {
		color: theme.palette.brand,
		fontWeight: 600,
		fontSize: "0.8125rem",
		lineHeight: 1.2,
	},
}));

const METRICS = [
	{ key: "active", label: "Active" },
	{ key: "trial", label: "Trial" },
	{ key: "paymentIssue", label: "Payment issue" },
	{ key: "none", label: "No subscription" },
	{ key: "disabled", label: "Disabled", hideWhenZero: true },
	{ key: "nz", label: "New Zealand" },
	{ key: "export", label: "Export" },
];

const AccountSummary = ({ organisations, selected, onSelect }) => {
	const classes = useStyles();
	const summary = useMemo(() => summariseAccounts(organisations), [organisations]);

	const onCardClick = (key) => () => onSelect(selected === key ? null : key);

	const onCardKeyDown = (key) => (event) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onCardClick(key)();
		}
	};

	return (
		<div className={classes.root} data-cy="account-summary">
			{METRICS.filter(
				({ key, hideWhenZero }) => !hideWhenZero || summary[key] > 0 || selected === key
			).map(({ key, label }) => (
				<Card
					key={key}
					className={clsx(classes.card, selected === key && classes.selected)}
					onClick={onCardClick(key)}
					onKeyDown={onCardKeyDown(key)}
					role="button"
					tabIndex={0}
					aria-pressed={selected === key}
					data-cy={`account-summary-${key}`}
				>
					<CardContent className={classes.content}>
						<Typography className={classes.label} component="span">
							{label}
						</Typography>
						<Typography className={classes.value} component="span">
							{summary[key]}
						</Typography>
					</CardContent>
				</Card>
			))}
		</div>
	);
};

AccountSummary.propTypes = {
	organisations: PropTypes.array,
	selected: PropTypes.string,
	onSelect: PropTypes.func.isRequired,
};

export default AccountSummary;
